import { ClientOnly, createFileRoute, Outlet } from "@tanstack/react-router";
import { Toaster } from "react-hot-toast";
import { GsaAnalytics } from "#/app/features/analytics/gsa-analytics";
import { GlobalAlerts } from "#/_packages/umbrella/features/alerts/components/global-alerts";
import { BackToTop } from "#/_packages/umbrella/features/back-to-top/components/back-to-top.tsx";
import {
	toBackToTopButtonVariant,
	toBackToTopVariant,
} from "#/_packages/umbrella/features/back-to-top/utils";
import CookieNotification from "#/_packages/umbrella/features/cookie-notification/components/cookie-notification.tsx";
import { apiLinkToUrl } from "#/_packages/umbrella/features/form-block/utils";
import PagePreviewBanner from "#/_packages/umbrella/features/previews/components/page-preview-banner.tsx";
import { CmsHtmlInjection } from "#/_packages/umbrella/features/routing/components/html-injection.tsx";
import {
	getLayoutLoaderData,
	umbrellaMetaLinks,
} from "#/_packages/umbrella/features/routing/utils";
import { UmbracoRendererProvider } from "#/_packages/umbrella/integrations/umbraco/utils/renderer-mappings.tsx";
import { SiteSettingsProvider } from "#/_packages/umbrella/hooks/site-settings-context";
import Footer from "#/_packages/umbrella/layout/footer.tsx";
import Header from "#/_packages/umbrella/layout/header.tsx";
import { siteMetaData, getThemeCssFromSiteSettings } from "#/_packages/umbrella/utils/seo-utils.ts";
import { gsaBlockGridItemRenderers } from "#/app/features/rendering/components/blocktype-renderer.tsx";
import { gsaContentTypeRenderers } from "#/app/features/rendering/components/doctype-renderer.tsx";
import { getSearchSettings } from "#/app/features/search/api/get-search-settings.ts";
import styles from "#/app/styles.css?url";

export const Route = createFileRoute("/_umbraco/_content")({
	staleTime: 5 * 60 * 1000,
	preloadStaleTime: 5 * 60 * 1000,
	component: AppLayoutComponent,
	loader: async () => {
		const [layoutData, searchSettings] = await Promise.all([
			getLayoutLoaderData(),
			getSearchSettings(),
		]);

		return {
			...layoutData,
			searchSettings,
		};
	},
	head: ({ loaderData }) => {
        const customHead = loaderData?.customHead;
		return {
			meta: [
				{
					charSet: "utf-8",
				},
                ...siteMetaData(loaderData?.globalSiteSettings),
                ...(customHead?.meta ?? []),
			],
			links: [
				...umbrellaMetaLinks(loaderData?.globalSiteSettings),
				{ rel: "stylesheet", href: styles },
                ...(customHead?.links ?? []),
			],
			styles: [
                ...getThemeCssFromSiteSettings(loaderData?.globalSiteSettings),
                ...(customHead?.styles ?? []),
            ],
		};
	},
});

function AppLayoutComponent() {
	const loaderData = Route.useLoaderData();
	return (
		<SiteSettingsProvider settings={loaderData.globalSiteSettings}>
			<GsaAnalytics />
			<div className="site-wrapper">

				<div>
					<PagePreviewBanner />
					<UmbracoRendererProvider
						blocks={gsaBlockGridItemRenderers}
						pages={gsaContentTypeRenderers}
					>
						<GlobalAlerts />
						<Header />
						<main>
							<Outlet />
							<BackToTop
								variant={toBackToTopVariant(
									loaderData.globalSiteSettings?.properties?.backToTopVariant,
								)}
								showIcon={
									loaderData.globalSiteSettings?.properties
										?.backToTopButtonIcon ?? false
								}
								buttonVariant={toBackToTopButtonVariant(
									loaderData.globalSiteSettings?.properties
										?.backToTopButtonVariant,
								)}
								visibility={
									loaderData.globalSiteSettings?.properties
										?.backToTopVisibility ?? undefined
								}
							/>
						</main>
						<Footer />
						{loaderData?.globalSiteSettings?.properties?.cookieToggle ? (
							<ClientOnly>
								<CookieNotification
									body={
										loaderData?.globalSiteSettings?.properties?.cookieNotification
											?.markup
									}
									link={apiLinkToUrl(
										loaderData?.globalSiteSettings?.properties?.cookiePage?.[0],
									)}
								/>
							</ClientOnly>
						) : null}
					</UmbracoRendererProvider>
					<Toaster position="bottom-right" reverseOrder={false} />
				</div>
				<CmsHtmlInjection
					target={"body"}
					html={
						loaderData?.globalSiteSettings?.properties
							?.globalCustomScriptsInBody ?? ""
					}
				/>
			</div>
		</SiteSettingsProvider>
	);
}
